import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { 
  Wallet, 
  Clock, 
  Banknote, 
  Calendar, 
  Loader2, 
  CheckCircle, 
  AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getPayoutBalance,
  getPayoutHistory,
  createPayoutSchedule,
  deletePayoutSchedule,
  requestManualPayout
} from '../../lib/api';

const MerchantPayoutsTab = ({ 
  payoutBalance, 
  setPayoutBalance, 
  payoutSchedule, 
  setPayoutSchedule, 
  payoutHistory, 
  setPayoutHistory 
}) => {
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [processingPayout, setProcessingPayout] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    min_payout_amount: 10
  });

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount < 10) {
      toast.error('Minimum payout amount is $10');
      return;
    }
    if (amount > (payoutBalance?.available_balance || 0)) {
      toast.error('Insufficient available balance');
      return;
    }
    
    setProcessingPayout(true);
    try {
      await requestManualPayout(amount);
      toast.success(`Payout of $${amount.toFixed(2)} processed successfully!`);
      setShowPayoutDialog(false);
      setPayoutAmount('');
      const [balanceRes, historyRes] = await Promise.all([
        getPayoutBalance(),
        getPayoutHistory()
      ]);
      setPayoutBalance(balanceRes.data);
      setPayoutHistory(historyRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      const data = {
        frequency: scheduleForm.frequency,
        min_payout_amount: scheduleForm.min_payout_amount,
        payout_currency: 'USD'
      };
      
      if (scheduleForm.frequency === 'weekly') {
        data.day_of_week = parseInt(scheduleForm.day_of_week);
      } else if (scheduleForm.frequency === 'monthly') {
        data.day_of_month = parseInt(scheduleForm.day_of_month);
      }
      
      const res = await createPayoutSchedule(data);
      setPayoutSchedule(res.data);
      setShowScheduleDialog(false);
      toast.success('Payout schedule created!');
      
      const balanceRes = await getPayoutBalance();
      setPayoutBalance(balanceRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleDeleteSchedule = async () => {
    try {
      await deletePayoutSchedule();
      setPayoutSchedule(null);
      toast.success('Payout schedule removed');
      
      const balanceRes = await getPayoutBalance();
      setPayoutBalance(balanceRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove schedule');
    }
  };

  return (
    <div className="space-y-6" data-testid="payouts-tab">
      {/* Balance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Wallet className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Available Balance</p>
                <p className="text-2xl font-bold">${payoutBalance?.available_balance?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">${payoutBalance?.pending_balance?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Banknote className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Paid Out</p>
                <p className="text-2xl font-bold">${payoutBalance?.total_paid_out?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" data-testid="request-payout-btn">
              <Banknote className="w-4 h-4 mr-2" />
              Request Payout
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Request Manual Payout</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-xl bg-muted">
                <p className="text-sm text-muted-foreground">Available Balance</p>
                <p className="text-2xl font-bold">${payoutBalance?.available_balance?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payout_amount">Payout Amount</Label>
                <Input
                  id="payout_amount"
                  type="number"
                  min="10"
                  step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="Minimum $10"
                  data-testid="payout-amount-input"
                />
              </div>
              <Button 
                onClick={handleRequestPayout} 
                disabled={processingPayout || !payoutAmount}
                className="w-full rounded-xl"
                data-testid="confirm-payout-btn"
              >
                {processingPayout ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Request Payout'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-xl" data-testid="schedule-payout-btn">
              <Calendar className="w-4 h-4 mr-2" />
              {payoutSchedule ? 'Update Schedule' : 'Set Up Automatic Payouts'}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Automatic Payout Schedule</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Payout Frequency</Label>
                <select
                  value={scheduleForm.frequency}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  data-testid="frequency-select"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              {scheduleForm.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <select
                    value={scheduleForm.day_of_week}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: parseInt(e.target.value) })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    data-testid="day-of-week-select"
                  >
                    {dayNames.map((day, idx) => (
                      <option key={day} value={idx}>{day}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {scheduleForm.frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label>Day of Month (1-28)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={scheduleForm.day_of_month}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_month: parseInt(e.target.value) })}
                    data-testid="day-of-month-input"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Minimum Payout Amount ($)</Label>
                <Input
                  type="number"
                  min="10"
                  value={scheduleForm.min_payout_amount}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, min_payout_amount: parseFloat(e.target.value) })}
                  data-testid="min-amount-input"
                />
                <p className="text-xs text-muted-foreground">
                  Payouts will only process when your balance exceeds this amount
                </p>
              </div>
              
              <Button onClick={handleCreateSchedule} className="w-full rounded-xl" data-testid="save-schedule-btn">
                Save Schedule
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Current Schedule */}
      {payoutSchedule && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Active Payout Schedule
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDeleteSchedule}
                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                data-testid="delete-schedule-btn"
              >
                Remove
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Frequency</p>
                <p className="font-medium capitalize">{payoutSchedule.frequency}</p>
              </div>
              {payoutSchedule.day_of_week !== null && payoutSchedule.day_of_week !== undefined && (
                <div>
                  <p className="text-sm text-muted-foreground">Day</p>
                  <p className="font-medium">{dayNames[payoutSchedule.day_of_week]}</p>
                </div>
              )}
              {payoutSchedule.day_of_month && (
                <div>
                  <p className="text-sm text-muted-foreground">Day of Month</p>
                  <p className="font-medium">{payoutSchedule.day_of_month}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Minimum Amount</p>
                <p className="font-medium">${payoutSchedule.min_payout_amount?.toFixed(2) || '10.00'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Next Payout</p>
                <p className="font-medium">
                  {payoutSchedule.next_payout_date 
                    ? new Date(payoutSchedule.next_payout_date).toLocaleDateString()
                    : 'TBD'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payout History */}
      <div>
        <h3 className="text-lg font-heading font-semibold mb-4">Payout History</h3>
        {payoutHistory.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Banknote className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No payouts yet</p>
              <p className="text-sm text-muted-foreground">Your payout history will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {payoutHistory.map((payout) => (
              <Card key={payout.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        payout.status === 'completed' ? 'bg-emerald-500/10' : 
                        payout.status === 'pending' ? 'bg-orange-500/10' : 'bg-red-500/10'
                      }`}>
                        {payout.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : payout.status === 'pending' ? (
                          <Clock className="w-5 h-5 text-orange-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium capitalize">{payout.payout_type} Payout</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(payout.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-emerald-600">${payout.amount.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground capitalize">{payout.status}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantPayoutsTab;
